import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import * as yup from "yup";
import API from "helpers/api";
import { isoToLocalInput, localInputToIso } from "helpers/date";
import { useDispatch } from "react-redux";
import { showError, showSuccess } from "../../redux/common/actions";
import { useMaintenanceBanner } from "contexts/MaintenanceBannerContext";

const URL_SCHEMA = yup.string().url();
const isInvalidUrl = (value) => Boolean(value) && !URL_SCHEMA.isValidSync(value);

const SEVERITIES = [
	{ value: "info", label: "Info (blue)" },
	{ value: "warning", label: "Warning (amber)" },
	{ value: "critical", label: "Critical (red)" },
];

const EMPTY = {
	message: "",
	severity: "warning",
	visibleFromLocal: "",
	startsAtLocal: "",
	endsAtLocal: "",
	link: "",
};

function MaintenanceBannerSection() {
	const dispatch = useDispatch();
	const { refresh } = useMaintenanceBanner();
	const [form, setForm] = useState(EMPTY);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [exists, setExists] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await API.get("/api/maintenance-banner/current");
			const data = res?.data;
			if (data) {
				setExists(true);
				setForm({
					message: data.message || "",
					severity: data.severity || "warning",
					visibleFromLocal: isoToLocalInput(data.visibleFrom),
					startsAtLocal: isoToLocalInput(data.startsAt),
					endsAtLocal: isoToLocalInput(data.endsAt),
					link: data.link || "",
				});
			} else {
				setExists(false);
				setForm(EMPTY);
			}
		} catch {
			setExists(false);
			setForm(EMPTY);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const setField = (key) => (e) =>
		setForm((prev) => ({ ...prev, [key]: e.target.value }));

	const handleSave = async () => {
		if (!form.message.trim()) {
			dispatch(showError("A banner message is required."));
			return;
		}
		if (isInvalidUrl(form.link)) {
			dispatch(showError("The link must be a valid URL."));
			return;
		}

		const startsAt = localInputToIso(form.startsAtLocal);
		const endsAt = localInputToIso(form.endsAtLocal);
		const visibleFrom = localInputToIso(form.visibleFromLocal) || startsAt;

		if (!startsAt || !endsAt) {
			dispatch(showError("Window start and end are required."));
			return;
		}
		if (new Date(endsAt) <= new Date(startsAt)) {
			dispatch(showError("Window end must be after the start."));
			return;
		}

		setSaving(true);
		try {
			await API.put("/api/maintenance-banner", {
				message: form.message.trim(),
				severity: form.severity,
				startsAt,
				endsAt,
				visibleFrom,
				link: form.link.trim() || null,
			});
			dispatch(showSuccess("Maintenance banner saved."));
			setExists(true);
			refresh();
		} catch {
			dispatch(showError("Could not save the banner."));
		} finally {
			setSaving(false);
		}
	};

	const handleClear = async () => {
		setSaving(true);
		try {
			await API.delete("/api/maintenance-banner");
			dispatch(showSuccess("Maintenance banner cleared."));
			setForm(EMPTY);
			setExists(false);
			refresh();
		} catch {
			dispatch(showError("Could not clear the banner."));
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 1, maxWidth: 640 }}>
			<Box sx={{ font: "700 22px 'Roboto Condensed', sans-serif", color: "#393939", mb: 0.5 }}>
				Maintenance banner
			</Box>
			<Box sx={{ fontSize: 13, color: "#545353", mb: 3 }}>
				There is a single banner. Saving replaces it; clearing removes it. It shows
				in-app from "Visible from" until the window end, and the countdown uses the window.
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
				<TextField
					label="Message"
					value={form.message}
					onChange={setField("message")}
					required
					multiline
					minRows={2}
					fullWidth
				/>
				<TextField label="Severity" value={form.severity} onChange={setField("severity")} select fullWidth>
					{SEVERITIES.map((s) => (
						<MenuItem key={s.value} value={s.value}>
							{s.label}
						</MenuItem>
					))}
				</TextField>
				<TextField
					label="Visible from"
					type="datetime-local"
					value={form.visibleFromLocal}
					onChange={setField("visibleFromLocal")}
					InputLabelProps={{ shrink: true }}
					helperText="When the banner first appears (defaults to the window start if left blank)."
					fullWidth
				/>
				<Box sx={{ display: "flex", gap: 2 }}>
					<TextField
						label="Window start"
						type="datetime-local"
						value={form.startsAtLocal}
						onChange={setField("startsAtLocal")}
						InputLabelProps={{ shrink: true }}
						required
						fullWidth
					/>
					<TextField
						label="Window end"
						type="datetime-local"
						value={form.endsAtLocal}
						onChange={setField("endsAtLocal")}
						InputLabelProps={{ shrink: true }}
						required
						fullWidth
					/>
				</Box>
				<TextField
					label="Details link"
					value={form.link}
					onChange={setField("link")}
					placeholder="https://emi.freshdesk.com/…"
					error={isInvalidUrl(form.link)}
					helperText={isInvalidUrl(form.link) ? "Enter a valid URL." : "Optional. Shows a Details button."}
					fullWidth
				/>

				<Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
					<Button
						onClick={handleClear}
						disabled={saving || !exists}
						variant="outlined"
						sx={{ color: "#E21313", borderColor: "#E21313" }}
					>
						Clear banner
					</Button>
					<Button
						onClick={handleSave}
						disabled={saving}
						variant="contained"
						sx={{ backgroundColor: "#23BB79" }}
					>
						{saving ? "Saving…" : "Save banner"}
					</Button>
				</Box>
			</Box>
		</Box>
	);
}

export default MaintenanceBannerSection;
